import { defineStore } from 'pinia';

import { useLoginUserStore } from '../login-user';

import {
  AutocompleteRepository,
  CompanyResponse,
} from '@/repositories/generated';

type CompanyAutocompleteRequestForm = {
  term: string | undefined;
  acquirerId: string | undefined;
};

type CompanyAutocompleteStoreState = {
  searchCache: Record<string, CompanyResponse>;
};

/**
 * 検索候補表示コンポーネント用にレスポンスをキャッシュするストア
 */
export const useCompanyAutocompleteStore = defineStore('companyAutocomplete', {
  state: (): CompanyAutocompleteStoreState => ({
    searchCache: {},
  }),
  getters: {
    // idを元にキャッシュ、ログイン情報から取得するcomputed。なければ自動補完APIを非同期で叩く
    getByIdOrDummy: (state) => (id: string): CompanyResponse => {
      const {
        isCompanyAutocompleteDisabled,
        loginCompany,
      } = useLoginUserStore().autocompleteModel;

      if (loginCompany?.id === id) {
        return loginCompany;
      }

      const cache = state.searchCache[id];
      if (cache) {
        return cache;
      }

      // 自動補完APIが１度だけ叩かれるように、キャッシュにダミーデータを差し込んでおく
      const dummyItem = { id, name: '' } as CompanyResponse;
      state.searchCache[id] = dummyItem;
      // キャッシュがないかつ権限がある場合は、自動補完APIを叩く
      if (!isCompanyAutocompleteDisabled) {
        (async () => {
          const res = await AutocompleteRepository.searchCompany({ term: id, acquirerId: undefined });
          res.forEach((it) => {
            state.searchCache[it.id] = it;
          });
        })();
      }
      return dummyItem;
    },
    // nameを元にキャッシュ、ログイン情報から取得するcomputed。なければ自動補完APIを非同期で叩く
    getByNameOrDummy: (state) => (name: string): CompanyResponse => {
      const {
        isCompanyAutocompleteDisabled,
        loginCompany,
      } = useLoginUserStore().autocompleteModel;

      if (loginCompany?.name === name) {
        return loginCompany;
      }

      const cache = Object.values(state.searchCache).find((it) => it.name === name);
      if (cache) {
        return cache;
      }

      // 自動補完APIが１度だけ叩かれるように、キャッシュにダミーデータを差し込んでおく
      const dummyItem = { id: '', name } as CompanyResponse;
      state.searchCache[name] = dummyItem;
      // キャッシュがないかつ権限がある場合は、自動補完APIを叩く
      if (!isCompanyAutocompleteDisabled) {
        (async () => {
          const res = await AutocompleteRepository.searchCompany({ term: name, acquirerId: undefined });
          res.forEach((it) => {
            state.searchCache[it.id] = it;
          });

          const cacheOfRealData = Object.values(state.searchCache).find((it) => it.name === name && it.id !== '');
          if (cacheOfRealData) {
            delete state.searchCache[name];
          }
        })();
      }
      return dummyItem;
    },
  },
  actions: {
    async search(form: CompanyAutocompleteRequestForm): Promise<CompanyResponse[]> {
      const res = await AutocompleteRepository.searchCompany(form);
      res.forEach((it) => {
        this.searchCache[it.id] = it;
      });
      return res;
    },
  },
});
